import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { 
    Plus, 
    Home,
    MapPin,
    Users,
    Edit,
    Trash2,
    CheckCircle,
    XCircle,
    Eye,
    AlertTriangle,
    Package,
    Clock,
    ClipboardList,
    UserCheck,
    Heart,
    PawPrint,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const LocationPicker = ({ position, setPosition }) => {
    useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        }
    });
    return position ? <Marker position={position} /> : null;
};

const statusLabels = {
    pending: 'En attente',
    approved: 'Approuvee',
    refused: 'Refusee',
    in_transit: 'En transit',
    delivered: 'Livree',
    partial: 'Partielle'
};

const statusStyles = {
    pending: 'warning',
    approved: 'success',
    refused: 'critical',
    in_transit: 'info',
    delivered: 'success',
    partial: 'warning'
};

const CenterDetailPanel = ({ centerId, token, onClose, canManage, allUsers }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddPersonnel, setShowAddPersonnel] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const headers = { Authorization: `Bearer ${token}` };

    const fetchDetail = async () => {
        try {
            const res = await axios.get(`${API}/care-centers/${centerId}/detail`, { headers });
            setDetail(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!centerId) return;
        fetchDetail();
    }, [centerId, token]);

    const availableUsers = useMemo(() => {
        const assigned = detail?.teams?.assigned_users || [];
        return (allUsers || []).filter(u => !assigned.some(a => a.id === u.id) && u.role !== 'pending');
    }, [allUsers, detail]);

    const handleAddPersonnel = async () => {
        try {
            for (const uid of selectedUserIds) {
                await axios.put(`${API}/care-centers/${centerId}/assign-personnel`, { user_id: uid }, { headers });
            }
            toast.success(`${selectedUserIds.length} personne(s) affectee(s)`);
            setSelectedUserIds([]);
            setShowAddPersonnel(false);
            fetchDetail();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleRemovePersonnel = async (userId) => {
        try {
            await axios.put(`${API}/care-centers/${centerId}/remove-personnel`, { user_id: userId }, { headers });
            toast.success('Personnel retire');
            fetchDetail();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!detail) return null;

    const { center, persons, teams, resources, material_requests } = detail;
    const occupancyPercent = center.capacity > 0 ? Math.round((center.current_count / center.capacity) * 100) : 0;

    return (
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1" data-testid="center-detail-panel">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-lg font-bold text-white">{center.name}</h2>
                    <p className="text-sm text-zinc-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{center.address}
                    </p>
                </div>
                <span className={`status-badge ${center.status === 'open' ? 'success' : 'critical'}`}>
                    {center.status === 'open' ? 'Ouvert' : 'Ferme'}
                </span>
            </div>

            {/* Occupation bar */}
            <div>
                <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">Occupation</span>
                    <span className="font-mono text-white">{center.current_count}/{center.capacity} ({occupancyPercent}%)</span>
                </div>
                <div className="w-full h-3 bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all ${occupancyPercent > 80 ? 'bg-red-500' : occupancyPercent > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(100, occupancyPercent)}%` }}
                    />
                </div>
            </div>

            {/* Persons Stats */}
            <div>
                <h3 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" /> Personnes ({persons.total_present})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                        <p className="text-lg font-bold text-red-400">{persons.to_evacuate}</p>
                        <p className="text-xs text-zinc-400">A evacuer</p>
                    </div>
                    <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-center">
                        <p className="text-lg font-bold text-amber-400">{persons.evacuating}</p>
                        <p className="text-xs text-zinc-400">En cours</p>
                    </div>
                    <div className="p-2 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                        <p className="text-lg font-bold text-green-400">{persons.evacuated}</p>
                        <p className="text-xs text-zinc-400">Evacuees</p>
                    </div>
                </div>
                <div className="flex gap-4 mt-2 text-xs text-zinc-400">
                    {persons.at_risk > 0 && (
                        <span className="flex items-center gap-1 text-red-400">
                            <AlertTriangle className="w-3 h-3" /> {persons.at_risk} a risque
                        </span>
                    )}
                    {persons.with_animals > 0 && (
                        <span className="flex items-center gap-1 text-amber-400">
                            <PawPrint className="w-3 h-3" /> {persons.with_animals} avec animaux
                        </span>
                    )}
                </div>
                {persons.list.length > 0 && (
                    <div className="mt-2 max-h-[120px] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead><tr className="text-zinc-500 border-b border-zinc-700">
                                <th className="text-left py-1 px-1">Nom</th>
                                <th className="text-left py-1 px-1">Statut</th>
                                <th className="text-left py-1 px-1">Etat psy.</th>
                            </tr></thead>
                            <tbody>
                                {persons.list.map(p => (
                                    <tr key={p.id} className="border-b border-zinc-800">
                                        <td className="py-1 px-1 text-white">{p.first_name} {p.last_name} {p.is_at_risk && <AlertTriangle className="w-3 h-3 inline text-red-400 ml-1" />}</td>
                                        <td className="py-1 px-1">
                                            <span className={`${p.evacuation_status === 'evacue' ? 'text-green-400' : p.evacuation_status === 'en_cours' ? 'text-amber-400' : 'text-red-400'}`}>
                                                {p.evacuation_status === 'evacue' ? 'Evacuee' : p.evacuation_status === 'en_cours' ? 'En cours' : 'A evacuer'}
                                            </span>
                                        </td>
                                        <td className="py-1 px-1">
                                            <span className={`${p.psychological_state === 'critique' ? 'text-red-400' : p.psychological_state === 'choque' ? 'text-amber-400' : 'text-green-400'}`}>
                                                {p.psychological_state}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Teams */}
            <div>
                <h3 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-green-400" /> Equipes de secours
                </h3>
                {teams.active_shifts.length > 0 ? (
                    <div className="space-y-1">
                        <p className="text-xs text-green-400 mb-1">{teams.active_count} personnel actif</p>
                        {teams.active_shifts.map(s => (
                            <div key={s.id} className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded text-sm">
                                <UserCheck className="w-4 h-4 text-green-400" />
                                <span className="text-white">{s.user_name}</span>
                                <span className="text-zinc-500 text-xs font-mono ml-auto">
                                    {new Date(s.start_time).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})} - {new Date(s.end_time).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-zinc-500">Aucun personnel actif</p>
                )}
                {teams.assigned_users.length > 0 && (
                    <div className="mt-2">
                        <p className="text-xs text-zinc-400 mb-1">Personnel affecte ({teams.assigned_users.length})</p>
                        <div className="flex flex-wrap gap-1">
                            {teams.assigned_users.map(u => (
                                <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-800 rounded text-xs text-zinc-300" data-testid={`center-user-${u.id}`}>
                                    {u.name} <span className="text-zinc-500">({u.role})</span>
                                    {canManage && (
                                        <button onClick={() => handleRemovePersonnel(u.id)} className="text-red-400 hover:text-red-300 ml-0.5" data-testid={`remove-center-user-${u.id}`}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {canManage && !showAddPersonnel && (
                    <Button size="sm" variant="outline" onClick={() => setShowAddPersonnel(true)} className="mt-2 h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10" data-testid="add-center-personnel-btn">
                        <Plus className="w-3 h-3 mr-1" /> Ajouter du personnel
                    </Button>
                )}
                {showAddPersonnel && (
                    <div className="mt-2 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg space-y-2" data-testid="add-personnel-panel">
                        <p className="text-xs text-zinc-400 font-medium">Selectionner les personnes a affecter :</p>
                        <div className="max-h-[200px] overflow-y-auto space-y-1">
                            {availableUsers
                                .map(u => {
                                    const checked = selectedUserIds.includes(u.id);
                                    return (
                                        <label key={u.id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors ${checked ? 'bg-green-500/10' : 'hover:bg-zinc-700/50'}`}>
                                            <input type="checkbox" checked={checked} onChange={(e) => {
                                                if (e.target.checked) setSelectedUserIds([...selectedUserIds, u.id]);
                                                else setSelectedUserIds(selectedUserIds.filter(x => x !== u.id));
                                            }} className="accent-green-500" data-testid={`center-personnel-check-${u.id}`} />
                                            <span className="text-xs text-white">{u.name}</span>
                                            <span className="text-[10px] text-zinc-500">({u.role})</span>
                                        </label>
                                    );
                                })
                            }
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => { setShowAddPersonnel(false); setSelectedUserIds([]); }} className="h-6 text-xs">Annuler</Button>
                            <Button size="sm" onClick={handleAddPersonnel} disabled={selectedUserIds.length === 0} className="tactical-btn h-6 text-xs" data-testid="confirm-add-center-personnel">
                                Affecter ({selectedUserIds.length})
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Resources */}
            <div>
                <h3 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-400" /> Ressources affectees
                </h3>
                {resources.list.length > 0 ? (
                    <div className="space-y-1">
                        {resources.list.map(r => (
                            <div key={r.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded text-sm">
                                <span className="text-white">{r.name} <span className="text-zinc-500">({r.type})</span></span>
                                <span className="font-mono text-zinc-300">{r.quantity_available}/{r.quantity}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-zinc-500">Aucune ressource affectee</p>
                )}
            </div>

            {/* Material Requests */}
            <div>
                <h3 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-cyan-400" /> Demandes de materiel
                </h3>
                <div className="flex gap-2 text-xs mb-2">
                    {material_requests.pending > 0 && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">En attente: {material_requests.pending}</span>}
                    {material_requests.approved > 0 && <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded">Approuvees: {material_requests.approved}</span>}
                    {material_requests.in_transit > 0 && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">En transit: {material_requests.in_transit}</span>}
                    {material_requests.delivered > 0 && <span className="px-2 py-0.5 bg-teal-500/20 text-teal-400 rounded">Livrees: {material_requests.delivered}</span>}
                    {material_requests.refused > 0 && <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded">Refusees: {material_requests.refused}</span>}
                </div>
                {material_requests.list.length > 0 ? (
                    <div className="space-y-1 max-h-[120px] overflow-y-auto">
                        {material_requests.list.map(r => (
                            <div key={r.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded text-xs">
                                <div>
                                    {r.items.map((item, i) => (
                                        <span key={`${item.resource_type}-${item.quantity}`} className="text-white">{i > 0 && ', '}{item.quantity}x {item.resource_type}</span>
                                    ))}
                                    <span className={`ml-2 ${r.urgency === 'critique' ? 'text-red-400' : r.urgency === 'urgent' ? 'text-amber-400' : 'text-zinc-500'}`}>
                                        ({r.urgency})
                                    </span>
                                </div>
                                <span className={`status-badge ${statusStyles[r.status] || 'warning'}`}>
                                    {statusLabels[r.status] || r.status}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-zinc-500">Aucune demande</p>
                )}
            </div>
        </div>
    );
};

const CareCentersPage = () => {
    const { token, canManageCenters, canCreateCenters, isChefDispositif, user } = useAuth();
    const { theme } = useTheme();
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedCenterId, setSelectedCenterId] = useState(null);
    const [editingCenter, setEditingCenter] = useState(null);
    const [position, setPosition] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        capacity: 50,
        phone: '',
        notes: ''
    });

    const headers = { Authorization: `Bearer ${token}` };

    const fetchCenters = async () => {
        try {
            const [centersRes, usersRes] = await Promise.all([
                axios.get(`${API}/care-centers`, { headers }),
                axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] }))
            ]);
            setCenters(centersRes.data);
            setAllUsers(usersRes.data.filter(u => u.role !== 'pending'));
        } catch (error) {
            console.error('Failed to fetch centers:', error);
            toast.error('Erreur lors du chargement des centres');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCenters();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!position) {
            toast.error('Veuillez selectionner une position sur la carte');
            return;
        }

        try {
            const data = {
                ...formData,
                latitude: position[0],
                longitude: position[1]
            };

            if (editingCenter) {
                await axios.put(`${API}/care-centers/${editingCenter.id}`, data, { headers });
                toast.success('Centre mis a jour');
            } else {
                await axios.post(`${API}/care-centers`, data, { headers });
                toast.success('Centre CARE cree');
            }

            setDialogOpen(false);
            resetForm();
            fetchCenters();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleEdit = (center) => {
        setEditingCenter(center);
        setFormData({
            name: center.name,
            address: center.address,
            capacity: center.capacity,
            phone: center.phone,
            notes: center.notes
        });
        setPosition([center.latitude, center.longitude]);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce centre ?')) return;
        try {
            await axios.delete(`${API}/care-centers/${id}`, { headers });
            toast.success('Centre supprime');
            fetchCenters();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleToggleStatus = async (center) => {
        try {
            const newStatus = center.status === 'open' ? 'closed' : 'open';
            await axios.put(`${API}/care-centers/${center.id}`, { status: newStatus }, { headers });
            toast.success(`Centre ${newStatus === 'open' ? 'ouvert' : 'ferme'}`);
            fetchCenters();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleViewDetail = (centerId) => {
        setSelectedCenterId(centerId);
        setDetailDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({ name: '', address: '', capacity: 50, phone: '', notes: '' });
        setPosition(null);
        setEditingCenter(null);
    };

    const totalCapacity = centers.reduce((acc, c) => acc + c.capacity, 0);
    const totalOccupancy = centers.reduce((acc, c) => acc + c.current_count, 0);
    const openCenters = centers.filter(c => c.status === 'open');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="care-centers-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="page-title">Centres d'Accueil (CARE)</h1>
                    <p className="page-subtitle">Points de regroupement et centres d'hebergement</p>
                </div>

                {canManageCenters && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        {canCreateCenters && (
                            <DialogTrigger asChild>
                                <Button className="tactical-btn" data-testid="add-center-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nouveau Centre
                                </Button>
                            </DialogTrigger>
                        )}
                        <DialogContent className="max-w-3xl bg-zinc-900 border-zinc-800">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-semibold text-white">
                                    {editingCenter ? 'Modifier le centre' : 'Nouveau centre CARE'}
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Nom du centre</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="tactical-input"
                                            placeholder="Ex: Gymnase Jean Jaures"
                                            required
                                            data-testid="center-name-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <Label className="form-label">Capacite (personnes)</Label>
                                        <Input
                                            type="number"
                                            value={formData.capacity}
                                            onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                                            className="tactical-input"
                                            min="1"
                                            required
                                            data-testid="center-capacity-input"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Adresse</Label>
                                    <Input
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        className="tactical-input"
                                        placeholder="Adresse complete"
                                        required
                                        data-testid="center-address-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Telephone</Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="tactical-input"
                                        placeholder="Numero de contact"
                                        data-testid="center-phone-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <Label className="form-label flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Position (cliquez sur la carte)
                                    </Label>
                                    <div className="h-[200px] rounded-lg overflow-hidden border border-zinc-700">
                                        <MapContainer
                                            center={position || [46.603354, 1.888334]}
                                            zoom={position ? 12 : 6}
                                            className="h-full w-full"
                                        >
                                            <TileLayer
                                                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <LocationPicker position={position} setPosition={setPosition} />
                                        </MapContainer>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <Label className="form-label">Notes</Label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="tactical-input"
                                        placeholder="Equipements disponibles, acces PMR..."
                                        data-testid="center-notes-input"
                                    />
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>
                                        Annuler
                                    </Button>
                                    <Button type="submit" className="tactical-btn" data-testid="center-submit-btn">
                                        {editingCenter ? 'Mettre a jour' : 'Creer le centre'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
                            <Home className="w-5 h-5 text-cyan-500" />
                            Detail du centre
                        </DialogTitle>
                    </DialogHeader>
                    {selectedCenterId && (
                        <CenterDetailPanel
                            centerId={selectedCenterId}
                            token={token}
                            onClose={() => setDetailDialogOpen(false)}
                            canManage={canManageCenters}
                            allUsers={allUsers}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="stat-card border-l-blue-500">
                    <CardContent className="p-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="stat-value">{centers.length}</p>
                                <p className="stat-label">Centres total</p>
                            </div>
                            <Home className="w-8 h-8 text-blue-500/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card border-l-green-500">
                    <CardContent className="p-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="stat-value">{openCenters.length}</p>
                                <p className="stat-label">Centres ouverts</p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-green-500/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card border-l-purple-500">
                    <CardContent className="p-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="stat-value">{totalOccupancy}</p>
                                <p className="stat-label">Personnes accueillies</p>
                            </div>
                            <Users className="w-8 h-8 text-purple-500/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="stat-card border-l-amber-500">
                    <CardContent className="p-0">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="stat-value">{totalCapacity}</p>
                                <p className="stat-label">Capacite totale</p>
                            </div>
                            <Home className="w-8 h-8 text-amber-500/50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Centers Table */}
            <Card className="content-card">
                <CardHeader className="card-header">
                    <CardTitle className="card-title flex items-center gap-2">
                        <Home className="w-5 h-5 text-blue-500" />
                        Liste des centres CARE
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">Nom</TableHead>
                                <TableHead className="text-zinc-400">Adresse</TableHead>
                                <TableHead className="text-zinc-400">Occupation</TableHead>
                                <TableHead className="text-zinc-400">Statut</TableHead>
                                <TableHead className="text-zinc-400">Telephone</TableHead>
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {centers.map((center) => {
                                const occupancyPercent = center.capacity > 0 ? Math.round((center.current_count / center.capacity) * 100) : 0;
                                return (
                                    <TableRow key={center.id} className="border-zinc-800 cursor-pointer hover:bg-zinc-800/50" data-testid={`center-row-${center.id}`} onClick={() => handleViewDetail(center.id)}>
                                        <TableCell className="font-medium text-white">{center.name}</TableCell>
                                        <TableCell className="text-zinc-400 max-w-[200px]">
                                            <span className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {center.address}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-white">{center.current_count}/{center.capacity}</span>
                                                <div className="w-16 h-2 bg-zinc-700 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full ${occupancyPercent > 80 ? 'bg-red-500' : occupancyPercent > 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                                                        style={{ width: `${occupancyPercent}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`status-badge ${center.status === 'open' ? 'success' : 'critical'}`}>
                                                {center.status === 'open' ? 'Ouvert' : 'Ferme'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-zinc-400">{center.phone || '-'}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleViewDetail(center.id)}
                                                    className="text-cyan-400 hover:bg-cyan-500/10"
                                                    data-testid={`view-detail-btn-${center.id}`}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {canManageCenters && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleToggleStatus(center)}
                                                            className={center.status === 'open' ? 'text-red-500 hover:bg-red-500/10' : 'text-green-500 hover:bg-green-500/10'}
                                                        >
                                                            {center.status === 'open' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => handleEdit(center)} className="tactical-btn-ghost">
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => handleDelete(center.id)} className="text-red-500 hover:bg-red-500/10">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {centers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                                        <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        Aucun centre CARE cree
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default CareCentersPage;
